/**
 * Die Zuweisungsauswahl — geteilt zwischen `task-assignee` (Spec 06a, „wer
 * diese Aufgabe bekommt") und `duedate-task-assignee` (Spec 06b, „wer die aus
 * dem Termin erzeugte Aufgabe bekommt", ADR 0017 Punkt 5). Ein `<select>`
 * über `GET /access/users?can_see=<subjectId>` (`useAssignableUsers`,
 * `lib/assignable-users.ts`) — die Kandidatenliste ist auf ~10 interne Nutzer
 * plus einzelne Externe ausgelegt, dieselbe Zurückhaltung wie
 * `RoleSelectField`.
 *
 * Die erste Option ist immer „Nicht zugewiesen" — ein echter, wählbarer Wert
 * (`''`), kein gesperrter Platzhalter: Ohne Zuständigen ist der Regelfall
 * beim Anlegen, keine unvollständige Eingabe.
 *
 * Liegt in `components/`, nicht in `features/tasks/`: Spec 06b nennt wörtlich
 * `features/tasks/TaskAssigneeField.tsx` als Bauform für
 * `duedate-task-assignee`, „nur mit anderem Bezeichner — keine zweite
 * Auswahlkomponente" (AK-568). `features/*` dürfen einander aber nicht
 * importieren (eslint-boundaries, `apps/web/CLAUDE.md`: „Gemeinsames gehört
 * nach components/ oder lib/") — genau dieselbe Verschiebung, die
 * `FormField.tsx` bereits hinter sich hat. `label` und `apiField` sind jetzt
 * Props statt fest verdrahtet, weil die beiden Aufrufer unterschiedliche
 * Beschriftungen („Zuständig" / „Zuständig für die Aufgabe") und
 * API-Feldnamen (`assignee_id` / `task_assignee_id`) tragen.
 */

import { useAssignableUsers } from '@/lib/assignable-users'
import { EnumSelectField } from '@/components/EnumSelectField'
import type { EnumOption } from '@/components/EnumSelectField'
import type { FieldError } from '@/components/FormField'

const UNASSIGNED: EnumOption = { value: '', label: 'Nicht zugewiesen' }

interface AssigneeFieldProps {
  readonly subjectId: string
  readonly testId: string
  readonly label: string
  readonly apiField: string
  readonly errorPrefix: string
  readonly value: string
  readonly onChange: (value: string) => void
  readonly error: FieldError | undefined
}

export function AssigneeField({
  subjectId,
  testId,
  label,
  apiField,
  errorPrefix,
  value,
  onChange,
  error,
}: AssigneeFieldProps) {
  const usersQuery = useAssignableUsers(subjectId)
  const options: EnumOption[] = [
    UNASSIGNED,
    ...(usersQuery.data?.items ?? []).map((person) => ({
      value: person.id,
      label: person.display_name ?? person.email,
    })),
  ]

  return (
    <EnumSelectField
      label={label}
      testId={testId}
      apiField={apiField}
      errorPrefix={errorPrefix}
      options={options}
      isPending={usersQuery.isPending}
      value={value}
      onChange={onChange}
      error={error}
    />
  )
}
